import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@dashboard/components/ui/dialog';
import { toast } from '@dashboard/context/ToastContext';
import { useAIPageResetStatus, useResetAIPage } from '@dashboard/hooks/queries/useAIQueries';
import getPageTypeLabel, { getPageTypeHeading } from '@dashboard/services/getPageTypeLabel';
import PanelTooltip from './PanelTooltip';

export const resetFailedMessage = label => `Couldn't reset the ${label}. Try again.`;

export default function ResetPageButton({ page, isStreaming, onReset }) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: status } = useAIPageResetStatus(page._id);
  const resetPage = useResetAIPage(page._id);
  const label = getPageTypeLabel(page);
  const heading = getPageTypeHeading(page);

  if (!status?.canReset) return null;

  const handleReset = () =>
    resetPage.mutate(undefined, {
      onSuccess: () => {
        setIsOpen(false);
        onReset();
        toast({ title: `${heading} reset`, type: 'success' });
      },
      onError: error => {
        setIsOpen(false);
        toast({ title: error?.message || resetFailedMessage(label), type: 'error' });
      },
    });

  return (
    <>
      <PanelTooltip label={`Reset ${label}`}>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Reset ${label}`}
          disabled={isStreaming}
          onClick={() => setIsOpen(true)}
        >
          <RotateCcw aria-hidden="true" />
        </Button>
      </PanelTooltip>
      <Dialog open={isOpen} onOpenChange={open => !resetPage.isPending && setIsOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset this {label}?</DialogTitle>
            <DialogDescription>
              Everything on this {label} goes back to a blank {label} and the AI chat is cleared.
              This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={resetPage.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleReset} disabled={resetPage.isPending}>
              Reset {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
